import { useState } from 'react'

import { Avatar, Box, Skeleton, Text } from '@factorialco/deprecated-design-system'
import {
  EmployeeForSelectFragment,
  EmployeeSelectorDocument,
  EmployeeSelectorQuery,
} from 'generated/resources/graphql'

import type {
  EmployeeSelectorMultipleNoAccess,
  EmployeeSelectorMultipleUsingAccess,
  EmployeeSelectorSingleNoAccess,
  EmployeeSelectorSingleUsingAccess,
} from 'modules/core/components/EntitySelectWrapper/Entities/Employees/types'
import { EntitySelector } from 'modules/core/components/EntitySelector'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import buildUrl from 'modules/core/lib/buildUrl'
import { getEmployeeDisplayName } from 'modules/core/lib/employeeName'
import i18n from 'modules/core/lib/i18n'

const isEmployeeSelectorSingleProps = (
  props:
    | EmployeeSelectorSingleUsingAccess
    | EmployeeSelectorSingleNoAccess
    | EmployeeSelectorMultipleNoAccess
    | EmployeeSelectorMultipleUsingAccess
): props is EmployeeSelectorSingleUsingAccess | EmployeeSelectorSingleNoAccess => {
  return !props.multiple
}

const EmployeeItem = ({
  employee,
  withAvatar,
}: {
  employee: EmployeeForSelectFragment
  withAvatar: boolean
}) => {
  const displayName = getEmployeeDisplayName(employee)
  return (
    <Box gap="s8" flexDirection="row" alignItems="center" key={employee.id}>
      {withAvatar && (
        <Avatar label={displayName} url={buildUrl(employee.access.avatar)} size="s32" />
      )}
      <Text.Normal ellipsis size="300">
        {displayName}
      </Text.Normal>
    </Box>
  )
}

const EmployeeSkeleton = ({ withAvatar }: { withAvatar: boolean }) => {
  return (
    <Box gap="s8" flexDirection="row" alignItems="center">
      {withAvatar && <Skeleton width="s32" height="s32" />}
      <Skeleton width="s240" height="s32" />
    </Box>
  )
}

export const EntitySelectorWrapper = (
  props:
    | EmployeeSelectorSingleUsingAccess
    | EmployeeSelectorSingleNoAccess
    | EmployeeSelectorMultipleNoAccess
    | EmployeeSelectorMultipleUsingAccess
) => {
  const {
    companyId,
    legalEntityIds,
    employeeIds,
    error,
    mandatory,
    label,
    emptyLabel,
    onlyManagers,
    onlyMyReportees,
    onlyDirectReports,
    width,
    minWidth,
    onPopoverClose,
    alwaysOpen = false,
    withAvatar = true,
    disabled = false,
    excludeCurrentEmployee = false,
    excludeIds = [],
    includeInactive = false,
    hideSearchIcon = false,
    hideBorder = false,
  } = props
  const { company, employee: currentEmployee } = useCurrentSession()
  const [companyIdState] = useState<number>(companyId ?? company.id)

  if (excludeCurrentEmployee && currentEmployee) {
    excludeIds.push(currentEmployee.id)
  }

  const renderEntity = (entity: EmployeeForSelectFragment) => (
    <EmployeeItem withAvatar={withAvatar} employee={entity} />
  )

  const variables = {
    companyId: companyIdState,
    ids: employeeIds,
    legalEntityIds,
    active: !includeInactive,
    reportees: onlyMyReportees,
    onlyManagers: onlyManagers,
    directReports: onlyDirectReports,
    excludeIds,
  }

  const extractConnection = (data: EmployeeSelectorQuery) => {
    return data.employees.employeeNamesConnection
  }

  if (!isEmployeeSelectorSingleProps(props)) {
    return (
      <EntitySelector.Multiple
        label={label}
        disabled={disabled}
        error={error}
        minWidth={minWidth}
        width={width}
        mandatory={mandatory}
        alwaysOpen={alwaysOpen}
        document={EmployeeSelectorDocument}
        extractConnection={extractConnection}
        renderEntity={renderEntity}
        renderSkeleton={() => <EmployeeSkeleton withAvatar={withAvatar} />}
        variables={variables}
        hideBorder={hideBorder}
        // eslint-disable-next-line no-type-assertion/no-type-assertion
        value={props.value as EmployeeForSelectFragment[]}
        onChange={props.onSelect}
        onPopoverClose={onPopoverClose}
        hideSearchIcon={hideSearchIcon}
        renderValue={(entities) => (
          <Text.Normal ellipsis>
            {entities.length > 0
              ? entities.map((el) => getEmployeeDisplayName(el)).join(', ')
              : (emptyLabel ?? i18n.tp('employees.select_employee', { count: 0 }))}
          </Text.Normal>
        )}
      />
    )
  }

  return (
    <EntitySelector
      label={label}
      disabled={disabled}
      error={error}
      mandatory={mandatory}
      document={EmployeeSelectorDocument}
      width={width}
      minWidth={minWidth}
      onChange={props.onSelect}
      onPopoverClose={onPopoverClose}
      // eslint-disable-next-line no-type-assertion/no-type-assertion
      value={(props.value ?? null) as EmployeeForSelectFragment | null}
      renderEntity={renderEntity}
      renderSkeleton={() => <EmployeeSkeleton withAvatar={withAvatar} />}
      variables={variables}
      extractConnection={extractConnection}
      hideSearchIcon={hideSearchIcon}
      renderValue={(entity) => (
        <Text.Normal ellipsis>
          {entity !== null
            ? getEmployeeDisplayName(entity)
            : (emptyLabel ?? i18n.tp('employees.select_employee', { count: 1 }))}
        </Text.Normal>
      )}
    />
  )
}
